import { useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { useAuth } from '@/components/auth/AuthProvider';
import { invokeProtectedFunction } from '@/lib/invokeProtectedFunction';
import { LiveCountContext } from '@/hooks/useLiveUserCount';

const PRESENCE_CHANNEL = 'site-presence';
const PRESENCE_STORAGE_KEY = 'workshop_presence_id';

function isPresenceSetupError(error: unknown) {
  if (!(error instanceof Error)) {
    return false;
  }

  const message = error.message.toLowerCase();
  return (
    message.includes('websocket not available') ||
    message.includes('the operation is insecure') ||
    message.includes('securityerror')
  );
}

function shouldEnablePresence() {
  if (typeof window === 'undefined') {
    return false;
  }

  if (!window.isSecureContext) {
    return false;
  }

  return window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1';
}

function getAnonymousPresenceId() {
  if (typeof window === 'undefined') return null;

  try {
    const existing = window.localStorage.getItem(PRESENCE_STORAGE_KEY);
    if (existing) return existing;

    const created =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `anon-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
    window.localStorage.setItem(PRESENCE_STORAGE_KEY, created);
    return created;
  } catch {
    return null;
  }
}

// ── Provider ──────────────────────────────────────────────────────────────────
// Manages the single Realtime Presence channel for the whole app.
// Listeners are registered BEFORE subscribe() is called so the initial
// "sync" event is never missed.

export function PresenceProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [liveCount, setLiveCount] = useState<number | null>(null);
  const lastReportedPeakRef = useRef(0);
  const presenceEnabled = shouldEnablePresence();
  const presenceId = user?.id ? `user:${user.id}` : getAnonymousPresenceId();

  useEffect(() => {
    if (!presenceEnabled || !presenceId) {
      setLiveCount(null);
      return;
    }

    let isActive = true;
    let channel: ReturnType<typeof supabase.channel> | null = null;

    const updateCount = () => {
      if (!channel || !isActive) {
        return;
      }

      const state = channel.presenceState<{ user_id: string }>();
      // Count unique user IDs (a user may have multiple tabs open)
      const ids = new Set(
        Object.values(state)
          .flat()
          .map((p) => p.user_id)
          .filter(Boolean)
      );
      const count = ids.size;
      setLiveCount(count);

      if (count <= lastReportedPeakRef.current) return;
      lastReportedPeakRef.current = count;
      void invokeProtectedFunction('track-presence-peak', {
        body: { peakConcurrentUsers: count },
      });
    };

    try {
      channel = supabase.channel(PRESENCE_CHANNEL);

      // Realtime should never be allowed to take down the app shell.
      channel
        .on('presence', { event: 'sync' }, updateCount)
        .on('presence', { event: 'join' }, updateCount)
        .on('presence', { event: 'leave' }, updateCount)
        .subscribe(async (status) => {
          if (status === 'CHANNEL_ERROR') {
            setLiveCount(null);
            return;
          }

          if (status === 'SUBSCRIBED' && presenceId) {
            try {
              await channel?.track({
                user_id: presenceId,
                online_at: new Date().toISOString(),
                authenticated: Boolean(user?.id),
              });
            } catch (error) {
              if (isPresenceSetupError(error)) {
                setLiveCount(null);
                return;
              }
              throw error;
            }
          }
        });
    } catch (error) {
      if (!isPresenceSetupError(error)) {
        throw error;
      }

      setLiveCount(null);
      return;
    }

    return () => {
      isActive = false;
      if (channel) {
        void supabase.removeChannel(channel);
      }
    };
  }, [presenceEnabled, presenceId, user?.id]);

  return (
    <LiveCountContext.Provider value={liveCount}>
      {children}
    </LiveCountContext.Provider>
  );
}
